import { useState, useMemo, useEffect, useCallback } from 'react'
import { Box, Paper, Row, Col, sx } from '@applift/factor'
import { DataGrid } from '@applift/datagrid'
import { CmpConversion } from '../../models/Conversion'
import { ConversionFilter } from '../../models/List'
import { WithRecordResponse } from '../../models/Response'
import { GlobalLoader } from '../GlobalLoader'
import { TestPixel } from '../../models/Pixel'
import { PixelTestDialog } from '../PixelTestDialog'
import { NoResultsOverlay } from '../NoResultsOverlay'
import { useSelectedConversionsDisplayTableData } from '../../hooks/useCmpConversions'
import { getColumnsOrder, getColumns } from './colDef'

interface Props {
  showActionColumn: boolean
  onSetConversionSelection: (conversions: CmpConversion[]) => void
  initialSelectedIds: number[]
  conversionType: ConversionFilter | null
  onLoadError: (e: any) => void
  disablePixelTest?: boolean
  tableHeight: string
  tableMaxHeight?: string
}

export const SelectedConversionsDisplayTable = (props: Props) => {
  const {
    showActionColumn,
    onSetConversionSelection,
    initialSelectedIds,
    conversionType,
    onLoadError,
    disablePixelTest,
    tableHeight,
    tableMaxHeight,
  } = props

  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [conversionTestData, setConversionTestData] =
    useState<TestPixel | null>(null)
  const [data, setData] = useState<WithRecordResponse<CmpConversion[]> | null>(
    null
  )

  const { data: initialData, isLoading } =
    useSelectedConversionsDisplayTableData(initialSelectedIds, {
      onError: onLoadError,
      onSuccess: (result: WithRecordResponse<CmpConversion[]>) => {
        onSetConversionSelection(result.data.data)
      },
      enabled: !!initialSelectedIds?.length,
    })

  useEffect(() => {
    if (initialData) {
      setData(initialData)
    }
  }, [initialData])

  useEffect(() => {
    if (conversionType) {
      setColumnOrder(getColumnsOrder(conversionType))
    }
  }, [conversionType])

  const onDeleteRow = useCallback(
    (id: number) => {
      if (!data) {
        return
      }

      const newData = {
        ...data,
        data: {
          ...data.data,
          filteredRecords: data.data.filteredRecords - 1,
          data: data.data.data.filter(row => row.id !== id),
        },
      }
      setData(newData)
      onSetConversionSelection(newData.data.data)
    },
    [data, onSetConversionSelection]
  )

  const colDef = useMemo(
    () =>
      getColumns({
        conversionType,
        showActionColumn,
        handleTestConversion: disablePixelTest
          ? undefined
          : setConversionTestData,
        handleDeleteConversion: onDeleteRow,
      }),
    [conversionType, showActionColumn, disablePixelTest, onDeleteRow]
  )

  const flatData = useMemo(() => {
    return data?.data?.data || []
  }, [data])

  const overlay = useMemo(() => {
    return !flatData?.length && !isLoading ? 'noResult' : undefined
  }, [flatData, isLoading])

  const filteredRecords = data?.data?.filteredRecords

  if (isLoading && !conversionType) {
    return <GlobalLoader />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Row>
        <Col xs={12}>
          <Paper
            variant="elevation"
            elevation={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Row>
              <Col xs={12}>
                <Box
                  sx={{ width: 100 }}
                  style={{
                    height: tableHeight,
                    maxHeight: tableMaxHeight,
                  }}
                >
                  <DataGrid
                    data={isLoading ? [] : flatData}
                    columns={colDef}
                    getRowId={(row: any) => `${row.id}`}
                    components={{
                      NoResultsOverlay: NoResultsOverlay,
                    }}
                    overlay={overlay}
                    hideHeader
                    hideFooter
                    density="compact"
                    loading={isLoading}
                    showColumnRightBorder
                    showCellRightBorder
                    disableRowSelectionOnClick
                    rowCount={data?.data?.data?.length || filteredRecords}
                    classes={{
                      root: sx({
                        borderRadius: 0,
                        border: 0,
                        borderTop: 1,
                        height: 100,
                      }),
                    }}
                    state={{ columnOrder }}
                  />
                </Box>
              </Col>
            </Row>
          </Paper>
        </Col>
      </Row>
      {!!conversionTestData && (
        <PixelTestDialog
          onClose={() => setConversionTestData(null)}
          testPixel={conversionTestData}
        />
      )}
    </Box>
  )
}
